import {
  toWSDateString,
  WSCard,
  WSDataItem,
  WSElement,
  WSGrid,
  WSInfoBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { PayStatement } from "@wingspanhq/wingspan-api-sdk";
import uniq from "lodash/uniq";
import React from "react";

import { PAYROLL_CURRENCY } from "../../../PayrollRuns/constants";
import { selectorPayStatementWorkLogIds } from "../../../PayrollRuns/selectors/selectorPayStatementWorkLogIds";
import { formatPayPeriodRange } from "../../../PayrollRuns/utils";
import { selectorPaystubPayBreakdown } from "../../selectors/selectorPaystubPayBreakdown";

const EMPTY_VALUE = "—";

type Props = {
  payStatement: PayStatement;
  /**
   * Payout destination for this pay. Optional because v3 cannot resolve it yet:
   * it needs PayoutSettings + ExternalBankAccount, gated by a payer→payee
   * bank-data grant (PLA-3198). Until then the card shows the fallbacks below.
   */
  payoutMethodLabel?: string;
  /** Masked payout account, e.g. "Chase Bank [XXXX2345]". */
  payoutAccountLabel?: string;
  /** Percentage of pay routed to the payout method, e.g. 100. */
  payoutPercentage?: number;
  onViewPaystub?: () => void;
};

export const RecentPayCard: React.FC<Props> = ({
  payStatement,
  payoutMethodLabel = EMPTY_VALUE,
  payoutAccountLabel = "",
  payoutPercentage = 0,
  onViewPaystub
}) => {
  const breakdown = selectorPaystubPayBreakdown(payStatement);
  const workLogsCount = uniq(
    selectorPayStatementWorkLogIds(payStatement)
  ).length;
  const netBreakdown = [
    { label: "Gross amount", amount: breakdown.grossAmount },
    { label: "Taxes", amount: breakdown.taxes },
    { label: "Benefits", amount: breakdown.benefits }
  ];

  return (
    <WSCard
      header={{
        label: formatPayPeriodRange(
          payStatement.payPeriodStartDate,
          payStatement.payPeriodEndDate
        ),
        value: {
          action: {
            label: "View paystub",
            onClick: onViewPaystub
          }
        }
      }}
    >
      <WSGrid gutter="L">
        <WSGrid.Item span={{ s: "12", l: "6" }}>
          <WSList gap="S">
            <WSInfoBox>
              <WSText.Paragraph color="gray600" mb="XS" weight="medium">
                Net payment amount
              </WSText.Paragraph>
              <WSText.Heading1 color="gray500" weight="book">
                {formatMoney(breakdown.netAmount, PAYROLL_CURRENCY)}
              </WSText.Heading1>
            </WSInfoBox>

            <WSInfoBox>
              <WSList gap="S">
                {netBreakdown.map(item => (
                  <WSDataItem
                    key={item.label}
                    inline
                    label={item.label}
                    value={formatMoney(item.amount, PAYROLL_CURRENCY)}
                  />
                ))}
              </WSList>
            </WSInfoBox>
          </WSList>
        </WSGrid.Item>

        <WSGrid.Item span={{ s: "12", l: "6" }}>
          <WSGrid gutter="L">
            <WSGrid.Item span={{ s: "12", l: "6" }}>
              <WSList gap="XL">
                <WSDataItem
                  label="Pay date"
                  value={
                    payStatement.payDate
                      ? toWSDateString(payStatement.payDate, "monthDayYear")
                      : EMPTY_VALUE
                  }
                />
                <WSElement>
                  <WSText.ParagraphSm color="gray500" mb="XS">
                    Payment method
                  </WSText.ParagraphSm>
                  <WSText.ParagraphSm>{payoutMethodLabel}</WSText.ParagraphSm>
                  <WSText.ParagraphSm>{payoutAccountLabel}</WSText.ParagraphSm>
                </WSElement>
              </WSList>
            </WSGrid.Item>

            <WSGrid.Item span={{ s: "12", l: "6" }}>
              <WSList gap="XL">
                <WSDataItem
                  label="Worklog"
                  value={`${workLogsCount} worklogs included`}
                />
                <WSDataItem label="Percentage" value={`${payoutPercentage}%`} />
              </WSList>
            </WSGrid.Item>
          </WSGrid>
        </WSGrid.Item>
      </WSGrid>
    </WSCard>
  );
};
